import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import AuthShell from "./components/AuthShell";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { updatePassword, session } = useAuth();

  const [checking, setChecking] = useState(true);
  const [recoveryReady, setRecoveryReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Supabase parses the recovery token from the URL hash and fires
  // a PASSWORD_RECOVERY auth event. If the URL has no token but we already
  // have a session, allow update anyway.
  useEffect(() => {
    let cancelled = false;

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        if (!cancelled) {
          setRecoveryReady(true);
          setChecking(false);
        }
      }
    });

    // Give Supabase a moment to detect the URL token, then settle state
    const t = setTimeout(async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data.session) setRecoveryReady(true);
      setChecking(false);
    }, 800);

    return () => {
      cancelled = true;
      clearTimeout(t);
      sub.subscription.unsubscribe();
    };
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    const { error: err } = await updatePassword(password);
    setLoading(false);
    if (err) {
      setError(err);
      return;
    }
    setDone(true);
    setTimeout(() => navigate("/dashboard", { replace: true }), 1500);
  };

  const tokenMissing = !checking && !recoveryReady && !session;

  return (
    <AuthShell
      title={done ? "Password updated." : "Set a new password."}
      subtitle={
        done
          ? "You're being redirected to your dashboard."
          : "Choose a strong password you'll remember."
      }
      footer={
        <>
          Remembered it?{" "}
          <Link
            to="/login"
            className="font-semibold text-brand-600 hover:text-brand-700 cursor-pointer"
          >
            Back to sign in
          </Link>
        </>
      }
    >
      {checking ? (
        <div className="flex items-center gap-2 text-sm text-ink-500">
          <i className="ri-loader-4-line animate-spin"></i>
          Verifying your reset link…
        </div>
      ) : tokenMissing ? (
        <div className="space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800 flex items-start gap-2">
            <i className="ri-error-warning-line text-base mt-0.5 shrink-0"></i>
            <span>
              This reset link is invalid or has expired. Request a fresh one from the sign-in
              page.
            </span>
          </div>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold bg-ink-900 text-white hover:bg-ink-800 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to sign in
          </Link>
        </div>
      ) : done ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3.5 py-3 text-sm text-emerald-800 flex items-start gap-2">
          <i className="ri-checkbox-circle-line text-base mt-0.5 shrink-0"></i>
          <span>
            Your password is all set. Taking you to the dashboard&hellip;
          </span>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3.5 py-3 text-sm text-rose-700 flex items-start gap-2">
              <i className="ri-error-warning-line text-base mt-0.5 shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-ink-700 mb-1.5 uppercase tracking-wider">
              New password
            </label>
            <div className="relative">
              <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
                <i className="ri-lock-2-line text-sm"></i>
              </span>
              <input
                type={showPwd ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="w-full text-sm pl-10 pr-10 py-3 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-ink-400 hover:text-ink-700 cursor-pointer"
              >
                <i className={showPwd ? "ri-eye-off-line" : "ri-eye-line"}></i>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-700 mb-1.5 uppercase tracking-wider">
              Confirm password
            </label>
            <input
              type={showPwd ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat password"
              className="w-full text-sm px-3 py-3 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Updating…
              </>
            ) : (
              <>
                Update password
                <i className="ri-arrow-right-line"></i>
              </>
            )}
          </button>
        </form>
      )}
    </AuthShell>
  );
}